import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { 
  Settings,
  Brain,
  Save,
  RotateCcw,
  RefreshCw,
  TestTube,
  AlertTriangle,
  CheckCircle,
  Loader,
  Bot,
  Target,
  BarChart3,
  MessageSquare,
  Building,
  Key,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { useTheme } from '../../../contexts/ThemeContext';

const AIConfiguration = () => {
  const { t } = useTranslation();
  const { user, hasRole } = useAuth();
  const { getProtectedTenantInfo } = useTenantProtection();
  const { isDark } = useTheme();
  
  // Full tenant AI configuration (matching TenantConfiguration structure)
  const [tenantAiConfig, setTenantAiConfig] = useState({
    models: {
      crmAnalysis: { service: 'openai', model: 'gpt-4o-mini', enabled: true },
      assistant: { service: 'openai', model: 'gpt-4o-mini', enabled: true },
      powerSales: { service: 'openai', model: 'gpt-4o-mini', enabled: true },
      businessCardScanner: { service: 'openai', model: 'gpt-4o', enabled: true, fallbackToOCR: true },
      leadScout: { service: 'openai', model: 'gpt-4o-mini', enabled: true, fallbackService: 'gemini' }
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({ crmAnalysis: false, assistant: false, powerSales: false });
  const [testMessage, setTestMessage] = useState('Hello, this is a test message');
  const [testResults, setTestResults] = useState({});

  // AI Services and Models from backend
  const [aiServices, setAiServices] = useState([]);
  const [aiDefaults, setAiDefaults] = useState({});
  const [apiKeys, setApiKeys] = useState({});

  // Check if user has access (Admin or Manager)
  const hasAccess = hasRole(['Admin', 'Manager']);

  // Get current tenant info safely
  const getCurrentTenant = () => {
    return getProtectedTenantInfo();
  };

  // Check if API key is configured and enabled for a service
  const hasApiKey = (serviceName) => {
    // Handle undefined or null serviceName
    if (!serviceName) {
      return false;
    }
    
    // OCR/Tesseract doesn't need API key
    if (serviceName === 'ocr' || serviceName === 'tesseract') {
      return true;
    }
    
    // Map service names to API key names
    const serviceToApiKeyMap = {
      'openai': 'openai',
      'gemini': 'gemini',
      'openrouter': 'openrouter',
      'huggingface': 'huggingface',
      'llava': 'huggingface'
    };
    
    const serviceNameLower = serviceName.toLowerCase();
    const apiKeyName = serviceToApiKeyMap[serviceNameLower];
    if (!apiKeyName) return false;
    
    const apiKeyConfig = apiKeys[apiKeyName];
    return apiKeyConfig && apiKeyConfig.enabled && apiKeyConfig.apiKey && apiKeyConfig.apiKey.trim() !== '';
  };

  // Get filtered services based on available API keys
  // applicationKey: specify application to determine if OCR should be included
  const getAvailableServices = (applicationKey = null) => {
    if (!aiServices || aiServices.length === 0) {
      return [];
    }
    
    return aiServices.filter(service => {
      if (!service || !service.service) return false;
      
      const serviceName = service.service.toLowerCase();
      
      // OCR/Tesseract only available for Business Card Scanner
      if (serviceName === 'ocr' || serviceName === 'tesseract') {
        return applicationKey === 'businessCardScanner';
      }
      
      return hasApiKey(service.service);
    });
  };

  // Load AI models configuration from backend
  const loadAiModelsConfig = async () => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/ai-config/models?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('AI Models API Response:', data);
        console.log('Services count:', data.data?.services?.length || 0);
        setAiServices(data.data.services || []);
        setAiDefaults(data.data.defaults || {});
      } else {
        console.error('AI Models API failed with status:', response.status);
        throw new Error('Failed to load AI models configuration');
      }
    } catch (error) {
      console.error('Failed to load AI models configuration:', error);
      toast.error('Failed to load AI models configuration');
    }
  };

  useEffect(() => {
    if (hasAccess) {
      // Load both AI models config and tenant config
      Promise.all([
        loadAiModelsConfig(),
        loadTenantAiConfiguration()
      ]);
    }
  }, [hasAccess]);

  // Validate and correct model values when AI services are loaded
  useEffect(() => {
    if (aiServices.length > 0 && tenantAiConfig.models) {
      let needsUpdate = false;
      const correctedConfig = { ...tenantAiConfig };
      
      Object.keys(correctedConfig.models).forEach(appKey => {
        const config = correctedConfig.models[appKey];
        const service = aiServices.find(s => s.service === config.service);
        const availableModels = service?.models || [];
        
        // Check if current model is valid for the selected service
        const isValidModel = availableModels.some(model => model.value === config.model);
        
        if (!isValidModel && availableModels.length > 0) {
          // Use first available model or default
          correctedConfig.models[appKey].model = availableModels[0].value;
          needsUpdate = true;
        }
      });
      
      if (needsUpdate) {
        setTenantAiConfig(correctedConfig);
      }
    }
  }, [aiServices, tenantAiConfig.models]);

  const loadTenantAiConfiguration = async () => {
    try {
      setLoading(true);
      const tenant = getCurrentTenant();
      
      // Use the regular tenant endpoint that works for Admin and Manager roles
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/ai-config/tenant/${tenant.code}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Tenant AI Config loaded:', data);
        setTenantAiConfig(data);
        
        // Extract API keys configuration
        if (data.apiKeys) {
          setApiKeys(data.apiKeys);
        }
      } else {
        console.error('Failed to load tenant config, status:', response.status);
        throw new Error('Failed to load configuration');
      }
    } catch (error) {
      console.error('Failed to load tenant AI configuration:', error);
      toast.error('Failed to load AI configuration');
      // Set defaults if load fails (use backend defaults if available)
      const defaultConfig = {
        models: {}
      };
      
      // Use backend defaults or fallback values
      ['assistant', 'crmAnalysis', 'powerSales'].forEach(app => {
        defaultConfig.models[app] = {
          service: aiDefaults[app]?.service || 'openai',
          model: aiDefaults[app]?.model || 'gpt-4o-mini',
          enabled: true
        };
      });
      
      // Add business card scanner with default OCR fallback
      defaultConfig.models.businessCardScanner = {
        service: aiDefaults.businessCardScanner?.service || 'openai',
        model: aiDefaults.businessCardScanner?.model || 'gpt-4o',
        enabled: true,
        fallbackToOCR: true
      };
      
      setTenantAiConfig(defaultConfig);
    } finally {
      setLoading(false);
    }
  };

  const saveTenantAiConfiguration = async () => {
    try {
      setSaving(true);
      const tenant = getCurrentTenant();
      
      // Load current config first to preserve all fields (prompts, parameters, apiKeys, etc.)
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const getResponse = await fetch(`${backendUrl}/api/ai-config/tenant/${tenant.code}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      let fullConfig = {};
      if (getResponse.ok) {
        fullConfig = await getResponse.json();
      } else {
        // If loading fails, use default structure
        fullConfig = {
          models: {},
          prompts: {},
          parameters: {},
          apiKeys: {}
        };
      }
      
      // Merge the updated models with the existing full config
      const configToSave = {
        ...fullConfig,
        models: {
          ...fullConfig.models,
          ...tenantAiConfig.models
        }
      };
      
      // Ensure required fields exist
      if (!configToSave.prompts) configToSave.prompts = {};
      if (!configToSave.parameters) configToSave.parameters = {};
      
      console.log('Saving AI configuration:', configToSave);
      
      const response = await fetch(`${backendUrl}/api/ai-config/tenant/${tenant.code}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(configToSave)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('AI configuration saved successfully:', result);
        toast.success('AI configuration saved successfully');
        
        // Reload the config to ensure UI is in sync
        await loadTenantAiConfiguration();
      } else {
        console.error('Failed to save AI configuration, status:', response.status);
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Error response:', errorData);
        toast.error(errorData.message || 'Failed to save configuration');
        throw new Error(errorData.message || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Failed to save tenant AI configuration:', error);
      toast.error(error.message || 'Failed to save AI configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateAiService = (applicationKey, serviceKey) => {
    setTenantAiConfig(prev => {
      const newService = aiServices.find(s => s.service === serviceKey);
      const availableModels = newService?.models || [];
      const currentModel = prev.models[applicationKey]?.model;
      
      // Check if current model exists in the new service's available models
      const modelExists = availableModels.some(model => model.value === currentModel);
      
      // If current model doesn't exist in new service, use first available model or default
      const newModel = modelExists 
        ? currentModel 
        : (availableModels[0]?.value || aiDefaults[applicationKey]?.model || 'gpt-4o-mini');
      
      return {
        ...prev,
        models: {
          ...prev.models,
          [applicationKey]: {
            ...prev.models[applicationKey],
            service: serviceKey,
            model: newModel
          }
        }
      };
    });
  };

  const updateAiModel = (applicationKey, modelKey) => {
    setTenantAiConfig(prev => ({
      ...prev,
      models: {
        ...prev.models,
        [applicationKey]: {
          ...prev.models[applicationKey],
          model: modelKey
        }
      }
    }));
  };

  const toggleService = (applicationKey) => {
    setTenantAiConfig(prev => ({
      ...prev,
      models: {
        ...prev.models,
        [applicationKey]: {
          ...prev.models[applicationKey],
          enabled: !prev.models[applicationKey].enabled
        }
      }
    }));
  };

  const testAiService = async (applicationKey) => {
    try {
      setTesting(prev => ({ ...prev, [applicationKey]: true }));
      const tenant = getCurrentTenant();
      const config = tenantAiConfig.models[applicationKey];
      
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/ai-config/test/${applicationKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          message: testMessage,
          tenant: tenant.code,
          service: config.service,
          model: config.model
        })
      });

      if (response.ok) {
        const result = await response.json();
        setTestResults(prev => ({ ...prev, [applicationKey]: { success: true, result } }));
        toast.success(`${applicationKey} test successful`);
      } else {
        throw new Error('Test failed');
      }
    } catch (error) {
      console.error(`Failed to test ${applicationKey}:`, error);
      setTestResults(prev => ({ ...prev, [applicationKey]: { success: false, error: error.message } }));
      toast.error(`${applicationKey} test failed`);
    } finally {
      setTesting(prev => ({ ...prev, [applicationKey]: false }));
    }
  };

  // Define metadata for known applications (must be before useMemo)
  const applicationMetadata = React.useMemo(() => ({
    salesBuddy: {
      name: 'SalesBuddy',
      description: 'Friendly CRM assistant with action capabilities - helps manage customer relationships',
      icon: MessageSquare,
      color: 'blue'
    },
    salesAssist: {
      name: 'SalesAssist',
      description: 'Strategic sales execution partner - focused on closing deals and achieving targets',
      icon: Target,
      color: 'purple'
    },
    salesAnalysis: {
      name: 'Sales Analysis',
      description: 'AI-powered sales data analysis and insights',
      icon: BarChart3,
      color: 'green'
    },
    businessCardScanner: {
      name: 'Business Card Scanner',
      description: 'AI-powered extraction of information from business cards (Thai & English)',
      icon: Bot,
      color: 'orange',
      hasFallback: true
    },
    leadGeneration: {
      name: 'Lead Generation',
      description: 'AI-powered lead generation from external sources (LinkedIn, websites, etc.)',
      icon: Sparkles,
      color: 'indigo'
    },
    leadScout: {
      name: 'Lead Scout',
      description: 'AI-powered lead scouting from customer information by searching social media and public websites',
      icon: Target,
      color: 'purple'
    },
    knowledgeAnalysis: {
      name: 'Knowledge Analysis',
      description: 'AI-powered analysis and rewriting of knowledge articles for chat optimization',
      icon: Brain,
      color: 'cyan'
    }
  }), []);

  // Dynamically generate aiApplications from tenantAiConfig.models
  const aiApplications = React.useMemo(() => {
    if (!tenantAiConfig?.models) return [];
    
    return Object.keys(tenantAiConfig.models).map(key => {
      const metadata = applicationMetadata[key];
      
      // If we have metadata, use it; otherwise generate defaults
      if (metadata) {
        return {
          key,
          ...metadata
        };
      }
      
      // Generate default metadata for unknown models
      const displayName = key
        .replace(/([A-Z])/g, ' $1') // Add space before capital letters
        .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
        .trim();
      
      return {
        key,
        name: displayName,
        description: `AI model configuration for ${displayName}`,
        icon: Brain,
        color: 'gray'
      };
    });
  }, [tenantAiConfig?.models, applicationMetadata]);

  // Show loading state if AI services are not loaded yet
  if (loading || aiServices.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="mx-auto h-8 w-8 animate-spin text-blue-600" />
          <p className="mt-2 text-sm text-gray-600">Loading AI configuration...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
          <h3 className={`mt-2 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Access Denied</h3>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
            You need Admin or Manager permissions to access AI Configuration.
          </p>
        </div>
      </div>
    );
  }

  const currentTenant = getCurrentTenant();

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          <p className="flex items-center space-x-2">
            <Building className="h-4 w-4" />
            <span className="text-sm">Configure AI models for {currentTenant.name} ({currentTenant.code})</span>
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => window.location.reload()}
            className={`flex items-center space-x-2 px-4 py-2 border rounded-md ${
              isDark 
                ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
          >
            <RotateCcw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={saveTenantAiConfiguration}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{saving ? 'Saving...' : 'Save Configuration'}</span>
          </button>
        </div>
      </div>

      {/* API Keys Status Warning */}
      {Object.keys(apiKeys).length > 0 && (
        <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="p-6">
            <h2 className={`text-lg font-medium mb-4 flex items-center space-x-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Key className="h-5 w-5 text-blue-600" />
              <span>API Keys Status</span>
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['openai', 'gemini', 'openrouter', 'huggingface'].map((serviceName) => {
                const apiKey = apiKeys[serviceName];
                const hasKey = apiKey && apiKey.enabled && apiKey.apiKey && apiKey.apiKey.trim() !== '';
                
                return (
                  <div key={serviceName} className={`p-3 rounded-lg border ${
                    hasKey 
                      ? isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'
                      : isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {serviceName.charAt(0).toUpperCase() + serviceName.slice(1)}
                      </span>
                      {hasKey ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                      )}
                    </div>
                    <p className={`text-xs mt-1 ${
                      hasKey 
                        ? isDark ? 'text-green-400' : 'text-green-700'
                        : isDark ? 'text-red-400' : 'text-red-700'
                    }`}>
                      {hasKey ? 'Configured' : 'Not configured'}
                    </p>
                  </div>
                );
              })}
            </div>
            
            <div className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
              <p className={`text-xs ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                <strong>Note:</strong> Only services with configured API keys will appear in the selection dropdowns below. 
                OCR (Tesseract) doesn't require an API key.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AI Services Configuration */}
      <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <h2 className={`text-lg font-medium mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>AI Services Configuration</h2>
          
          <div className="space-y-6">
            {aiApplications.map((application) => {
              const Icon = application.icon;
              const config = tenantAiConfig.models?.[application.key] || { service: 'openai', model: 'gpt-4o-mini', enabled: true };
              
              // Get only services with API keys (pass application key to determine OCR availability)
              const availableServices = getAvailableServices(application.key);
              
              // Check if current service has API key
              const currentServiceHasApiKey = hasApiKey(config?.service);
              
              // If no services available
              // For Business Card Scanner: show OCR at minimum
              // For others: show empty or first available with API key
              const displayServices = availableServices.length > 0 
                ? availableServices 
                : application.key === 'businessCardScanner'
                  ? aiServices.filter(s => s.service === 'ocr' || s.service === 'tesseract')
                  : [];
              
              // Find the current service - make sure it's from the filtered list if possible
              const currentService = aiServices.find(s => s && s.service && s.service === config?.service);
              const availableModels = currentService?.models || [];
              
              // Debug: log if models are missing
              if (availableModels.length === 0 && config?.service) {
                console.log('No models found for:', {
                  applicationKey: application.key,
                  configService: config.service,
                  currentServiceFound: !!currentService,
                  aiServicesCount: aiServices.length,
                  availableServicesCount: availableServices.length
                });
              }
              
              // Ensure the current model is valid for the selected service
              const isValidModel = availableModels.length > 0 && availableModels.some(model => model.value === config?.model);
              const displayModel = isValidModel ? config.model : (availableModels[0]?.value || config?.model || 'gpt-4o-mini');

              return (
                <div key={application.key} className={`border rounded-lg p-6 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : `bg-${application.color}-100`}`}>
                        <Icon className={`h-6 w-6 text-${application.color}-600`} />
          </div>
                      <div className="flex-1">
                        <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{application.name}</h3>
                        <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{application.description}</p>
                        
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                          {/* Enable/Disable Toggle */}
                          <div>
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={config.enabled}
                                onChange={() => toggleService(application.key)}
                                className={`rounded text-blue-600 focus:ring-blue-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
                              />
                              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Enabled</span>
                            </label>
                            
                            {/* Fallback to OCR Toggle (only for Business Card Scanner) */}
                            {application.hasFallback && (
                              <label className="flex items-center space-x-2 mt-2">
                                <input
                                  type="checkbox"
                                  checked={config.fallbackToOCR !== false}
                                  onChange={(e) => {
                                    setTenantAiConfig(prev => ({
                                      ...prev,
                                      models: {
                                        ...prev.models,
                                        [application.key]: {
                                          ...prev.models[application.key],
                                          fallbackToOCR: e.target.checked
                                        }
                                      }
                                    }));
                                  }}
                                  disabled={!config.enabled}
                                  className={`rounded text-orange-600 focus:ring-orange-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
                                />
                                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Fallback to OCR</span>
                              </label>
                            )}
                        </div>

                          {/* AI Service Selection */}
                  <div>
                            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              AI Service
                              {availableServices.length < aiServices.length && availableServices.length > 0 && (
                                <span className={`ml-2 text-xs ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                                  (Showing only services with API keys)
                                </span>
                              )}
                            </label>
                    <select
                              value={config?.service || 'ocr'}
                              onChange={(e) => updateAiService(application.key, e.target.value)}
                              disabled={!config?.enabled}
                              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                isDark 
                                  ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                                  : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                              }`}
                            >
                              {displayServices.length > 0 ? (
                                displayServices.map((service) => (
                                  <option key={service?.service || Math.random()} value={service?.service}>
                                    {service?.label || service?.service}
                                  </option>
                                ))
                              ) : (
                                <option value="">
                                  {application.key === 'businessCardScanner' 
                                    ? 'No services available' 
                                    : 'No AI services configured - Please add API keys'}
                                </option>
                              )}
                    </select>
                    {!currentServiceHasApiKey && (
                      <p className={`text-xs mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                        ⚠️ API key not configured for {currentService?.label || config.service}
                      </p>
                    )}
                  </div>

                          {/* Model Selection */}
                  <div>
                            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              AI Model
                            </label>
                            <select
                              value={displayModel}
                              onChange={(e) => updateAiModel(application.key, e.target.value)}
                              disabled={!config.enabled}
                              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                isDark 
                                  ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                                  : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                              }`}
                            >
                              {availableModels.length > 0 ? (
                                availableModels.map((model) => (
                                  <option key={model.value} value={model.value}>
                                    {model.label}
                                  </option>
                                ))
                              ) : (
                                <option value={config?.model || 'gpt-4o-mini'}>
                                  {config?.model || 'Loading models...'}
                                </option>
                              )}
                            </select>
                  </div>

                          {/* Test Button */}
                          <div className="flex items-end">
                            {application.key === 'businessCardScanner' ? (
                              <div className={`w-full px-4 py-2 text-center text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                <TestTube className="h-4 w-4 mx-auto mb-1 text-gray-400" />
                                <span>Test in Business Card Scanner page</span>
                              </div>
                            ) : application.key === 'knowledgeAnalysis' ? (
                              <button
                                onClick={async () => {
                                  try {
                                    setTesting(prev => ({ ...prev, knowledgeAnalysis: true }));
                                    const tenant = getCurrentTenant();
                                    const backendUrl = process.env.REACT_APP_API_URL || '';
                                    
                                    const response = await fetch(`${backendUrl}/api/rag/knowledge/process`, {
                                      method: 'POST',
                                      headers: {
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                        'X-Tenant-Code': tenant.code,
                                        'Content-Type': 'application/json'
                                      }
                                    });
                                    
                                    if (response.ok) {
                                      const result = await response.json();
                                      toast.success(`Knowledge processing completed: ${result.statistics?.totalProcessed || 0} articles processed`);
                                      setTestResults(prev => ({ ...prev, knowledgeAnalysis: { success: true, result } }));
                                    } else {
                                      throw new Error('Processing failed');
                                    }
                                  } catch (error) {
                                    console.error('Knowledge processing failed:', error);
                                    toast.error('Knowledge processing failed: ' + error.message);
                                    setTestResults(prev => ({ ...prev, knowledgeAnalysis: { success: false, error: error.message } }));
                                  } finally {
                                    setTesting(prev => ({ ...prev, knowledgeAnalysis: false }));
                                  }
                                }}
                                disabled={!config.enabled || testing[application.key]}
                                className={`w-full flex items-center justify-center space-x-2 px-4 py-2 border rounded-md disabled:opacity-50 ${
                                  isDark 
                                    ? 'border-cyan-600 text-cyan-300 bg-cyan-900/20 hover:bg-cyan-800/30' 
                                    : 'border-cyan-300 text-cyan-700 bg-cyan-50 hover:bg-cyan-100'
                                }`}
                              >
                                {testing[application.key] ? (
                                  <Loader className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-4 w-4" />
                                )}
                                <span>{testing[application.key] ? 'Processing...' : 'Process Now'}</span>
                              </button>
                            ) : (
                              <button
                                onClick={() => testAiService(application.key)}
                                disabled={!config.enabled || testing[application.key]}
                                className={`w-full flex items-center justify-center space-x-2 px-4 py-2 border rounded-md disabled:opacity-50 ${
                                  isDark 
                                    ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                                }`}
                              >
                                {testing[application.key] ? (
                                  <Loader className="h-4 w-4 animate-spin" />
                                ) : (
                                  <TestTube className="h-4 w-4" />
                                )}
                                <span>{testing[application.key] ? 'Testing...' : 'Test'}</span>
                              </button>
                            )}
                  </div>
                </div>

                        {/* Current Configuration Display */}
                        <div className={`mt-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Current: {currentService?.label || 'Unknown'} → {availableModels.find(m => m.value === displayModel)?.label || displayModel}
                          {!isValidModel && (
                            <span className="ml-2 text-yellow-600">(Auto-corrected from invalid model)</span>
                          )}
                        </div>

                        {/* Knowledge Analysis Specific Configuration */}
                        {application.key === 'knowledgeAnalysis' && (
                          <div className={`mt-4 p-4 rounded-lg ${isDark ? 'bg-cyan-900/20 border border-cyan-700' : 'bg-cyan-50 border border-cyan-200'}`}>
                            <div className="space-y-4">
                              <div>
                                <h4 className={`text-sm font-semibold mb-2 ${isDark ? 'text-cyan-300' : 'text-cyan-800'}`}>
                                  🤖 Advanced Knowledge Analysis Configuration
                                </h4>
                                <p className={`text-xs ${isDark ? 'text-cyan-400' : 'text-cyan-700'}`}>
                                  Configure AI models for extracting entities and generating optimized knowledge articles
                                </p>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Extraction Model */}
                                <div>
                                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-cyan-300' : 'text-cyan-800'}`}>
                                    Extraction Model
                                  </label>
                                  <select
                                    value={config?.extractionModel || 'gpt-4o-mini'}
                                    onChange={(e) => {
                                      setTenantAiConfig(prev => ({
                                        ...prev,
                                        models: {
                                          ...prev.models,
                                          knowledgeAnalysis: {
                                            ...prev.models.knowledgeAnalysis,
                                            extractionModel: e.target.value
                                          }
                                        }
                                      }));
                                    }}
                                    disabled={!config.enabled}
                                    className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-cyan-500 ${
                                      isDark 
                                        ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                                        : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                                    }`}
                                  >
                                    {availableModels.length > 0 ? (
                                      availableModels.map((model) => (
                                        <option key={model.value} value={model.value}>
                                          {model.label}
                                        </option>
                                      ))
                                    ) : (
                                      <option value="gpt-4o-mini">GPT-4o Mini</option>
                                    )}
                                  </select>
                                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    Model for extracting products/services from articles
                                  </p>
                                </div>

                                {/* Generation Model */}
                                <div>
                                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-cyan-300' : 'text-cyan-800'}`}>
                                    Generation Model
                                  </label>
                                  <select
                                    value={config?.generationModel || 'gpt-4o-mini'}
                                    onChange={(e) => {
                                      setTenantAiConfig(prev => ({
                                        ...prev,
                                        models: {
                                          ...prev.models,
                                          knowledgeAnalysis: {
                                            ...prev.models.knowledgeAnalysis,
                                            generationModel: e.target.value
                                          }
                                        }
                                      }));
                                    }}
                                    disabled={!config.enabled}
                                    className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-cyan-500 ${
                                      isDark 
                                        ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                                        : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                                    }`}
                                  >
                                    {availableModels.length > 0 ? (
                                      availableModels.map((model) => (
                                        <option key={model.value} value={model.value}>
                                          {model.label}
                                        </option>
                                      ))
                                    ) : (
                                      <option value="gpt-4o-mini">GPT-4o Mini</option>
                                    )}
                                  </select>
                                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    Model for generating chat-optimized articles
                                  </p>
                                </div>

                                {/* Batch Size */}
                                <div>
                                  <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-cyan-300' : 'text-cyan-800'}`}>
                                    Batch Size
                                  </label>
                                  <input
                                    type="number"
                                    value={config?.batchSize || 10}
                                    onChange={(e) => {
                                      setTenantAiConfig(prev => ({
                                        ...prev,
                                        models: {
                                          ...prev.models,
                                          knowledgeAnalysis: {
                                            ...prev.models.knowledgeAnalysis,
                                            batchSize: parseInt(e.target.value) || 10
                                          }
                                        }
                                      }));
                                    }}
                                    disabled={!config.enabled}
                                    min="1"
                                    max="50"
                                    className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-cyan-500 ${
                                      isDark 
                                        ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                                        : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                                    }`}
                                  />
                                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    Number of articles to process per batch (1-50)
                                  </p>
                                </div>
                              </div>

                              {/* Info Box */}
                              <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                                <p className={`text-xs ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                                  <strong>ℹ️ How it works:</strong>
                                </p>
                                <ul className={`text-xs mt-2 space-y-1 ml-4 list-disc ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                                  <li><strong>Extraction Model:</strong> Analyzes articles to identify products, services, and events</li>
                                  <li><strong>Generation Model:</strong> Rewrites articles into chat-friendly format with proper structure</li>
                                  <li><strong>Batch Size:</strong> Controls how many articles are processed simultaneously (lower = more stable, higher = faster)</li>
                                  <li><strong>Auto-triggered:</strong> Runs automatically when knowledge articles are created or updated</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Business Card Scanner Specific Info */}
                        {application.key === 'businessCardScanner' && (
                          <div className={`mt-3 p-3 rounded-lg ${isDark ? 'bg-orange-900/20 border border-orange-700' : 'bg-orange-50 border border-orange-200'}`}>
                            <div className="text-xs space-y-1">
                              <p className={`font-medium ${isDark ? 'text-orange-300' : 'text-orange-800'}`}>
                                💡 Service Recommendations:
                              </p>
                              <p className={isDark ? 'text-orange-400' : 'text-orange-700'}>
                                <strong>OpenAI:</strong> Best for Thai text, high accuracy
                              </p>
                              <p className={isDark ? 'text-orange-400' : 'text-orange-700'}>
                                <strong>Gemini:</strong> Fast, accurate, good for multilingual
                              </p>
                              <p className={isDark ? 'text-orange-400' : 'text-orange-700'}>
                                <strong>OpenRouter:</strong> Free option, Llama 3.2 Vision
                              </p>
                              <p className={isDark ? 'text-orange-400' : 'text-orange-700'}>
                                <strong>OCR (Tesseract):</strong> Free, works offline, no API key needed (English focus)
                              </p>
                              <p className={`mt-2 ${isDark ? 'text-orange-400' : 'text-orange-700'}`}>
                                ✅ <strong>Fallback to OCR</strong> ensures extraction works even if AI service fails
                              </p>
                              <p className={`mt-1 text-xs ${isDark ? 'text-orange-500' : 'text-orange-600'}`}>
                                ℹ️ OCR is only available for Business Card Scanner, not for conversational AI
                              </p>
                            </div>
                            <div className={`mt-3 p-3 rounded-lg ${isDark ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                              <p className={`text-xs ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                                <strong>💡 Testing:</strong> Business Card Scanner requires an image file to test. 
                                Go to the <strong>Business Card Scanner</strong> page and upload a business card image to test the configured AI service.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Test Results */}
                        {testResults[application.key] && (
                          <div className={`mt-4 p-3 rounded-lg ${
                            testResults[application.key].success 
                              ? isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200' 
                              : isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'
                          }`}>
                            <div className="flex items-center space-x-2">
                              {testResults[application.key].success ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <AlertTriangle className="h-4 w-4 text-red-600" />
                              )}
                              <span className={`text-sm font-medium ${
                                testResults[application.key].success ? (isDark ? 'text-green-300' : 'text-green-800') : (isDark ? 'text-red-300' : 'text-red-800')
                              }`}>
                                {testResults[application.key].success ? 'Test Successful' : 'Test Failed'}
                              </span>
              </div>
                            {testResults[application.key].result && (
                              <p className="text-sm text-gray-600 mt-2">
                                Response: {JSON.stringify(testResults[application.key].result, null, 2)}
                              </p>
                            )}
                            {testResults[application.key].error && (
                              <p className="text-sm text-red-600 mt-2">
                                Error: {testResults[application.key].error}
                              </p>
                            )}
            </div>
          )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Test Message Input */}
      <div className={`rounded-lg shadow p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Test Configuration</h3>
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Test Message
          </label>
          <textarea
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
            rows={3}
            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              isDark 
                ? 'border-gray-600 bg-gray-700 text-white' 
                : 'border-gray-300 bg-white text-gray-900'
            }`}
            placeholder="Enter a test message to send to the AI services..."
          />
        </div>
      </div>
    </div>
  );
};

export default AIConfiguration;

